import React from "react";
import {
  Box,
  Heading,
  Text,
  Container,
  VStack,
  Flex,
  Avatar,
  Icon,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { FaQuoteLeft } from "react-icons/fa";
import ScrollAnimationWrapper from "./ScrollAnimationWrapper";

// Testimonial data
const testimonials = [
  {
    id: 1,
    name: "Ronit Sarna",
    role: "Software Developer",
    image: "/images/testimonials/ronit-sarna.jpg",
    text: "In the many times I've worked with Alwi, he's always effectively led us to success. He's always mindful of our aims and objectives and keeps our team motivated towards reaching our goals",
  },
  {
    id: 2,
    name: "Martynas Pocius",
    role: "Co-founder, NarrativeScapes",
    image: "/images/testimonials/martynas-pocius.jpg",
    text: "Alwi demonstrated exceptional agency right from the start. He quickly grasped our goals, took proactive steps without needing constant direction, and produced remarkable results in record time. We couldn't have asked for a better collaborator",
  },
  {
    id: 3,
    name: "Najiya Ilya",
    role: "CEO and Co-founder, SAPOT",
    image: "/images/testimonials/najiya-ilya.jpg",
    text: "Alwi is an independent thinker who helps problem-solve based on our needs. Rather than waiting for detailed instructions, he takes initiative to understand the challenges we face and works through them. He also delivers results quickly and reliably, keeping momentum going even when timelines are tight.",
  },
];

// Define keyframes for horizontal scroll
const scrollLeft = keyframes`
  0% { transform: translateX(0%); }
  100% { transform: translateX(-50%); }
`;

// Define keyframes for vertical scroll
const scrollTop = keyframes`
  0% { transform: translateY(0%); }
  100% { transform: translateY(-50%); }
`;

const TestimonialCard = ({ name, role, image, text, index }) => {
  return (
    <ScrollAnimationWrapper delay={0.1 * index} direction="up">
      <Box
        bg="brand.card"
        p={6}
        borderRadius="xl"
        boxShadow="md"
        borderWidth="1px"
        borderColor="brand.accent:alpha.30"
        position="relative"
        display="flex"
        flexDirection="column"
        height="100%"
        minH={{ base: "auto", md: "320px" }}
        transition="transform 0.3s, box-shadow 0.3s"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow: "lg",
        }}
      >
        <Icon
          as={FaQuoteLeft}
          w={8}
          h={8}
          color="brand.accent"
          opacity={0.4}
          mb={4}
          flexShrink={0}
        />
        <Text fontSize="md" mb={6} color="brand.text.secondary" flex="1">
          {text}
        </Text>
        <Flex align="center" flexShrink={0}>
          <Avatar
            src={image}
            name={name}
            mr={4}
            size="md"
            borderWidth="2px"
            borderColor="brand.accent"
          />
          <Box>
            <Text fontWeight="bold" fontSize="md">
              {name}
            </Text>
            <Text fontSize="sm" color="brand.text.secondary">
              {role}
            </Text>
          </Box>
        </Flex>
      </Box>
    </ScrollAnimationWrapper>
  );
};

const TestimonialsSection = () => {
  const visibleTestimonials = testimonials.slice(0, 3);
  // Duplicate testimonials for a seamless loop.
  // Ensure keys for React are unique if testimonial.id is used directly in TestimonialCard for its key,
  // but here the .map key will be the array index.
  const animatedDisplayTestimonials = [...visibleTestimonials, ...visibleTestimonials];

  return (
    <Box
      as="section"
      id="testimonials-section"
      bg="brand.primary"
      py={{ base: "4rem", md: "5rem" }}
      position="relative"
      overflow="hidden"
      borderTop="1px solid"
      borderColor="rgba(255, 255, 255, 0.05)"
    >
      {/* Background decorative elements */}
      <Box
        position="absolute"
        top="20%"
        left="-5%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="rgba(49, 130, 206, 0.07)"
        filter="blur(70px)"
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="-10%"
        right="-5%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="rgba(246, 173, 85, 0.05)"
        filter="blur(100px)"
        zIndex={0}
      />

      <Container maxW="1200px" position="relative" zIndex={1}>
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          {/* Section header */}
          <ScrollAnimationWrapper>
            <VStack spacing={3} align="center" textAlign="center">
              <Text
                color="brand.accent"
                fontWeight="600"
                letterSpacing="1px"
                textTransform="uppercase"
                fontSize="sm"
              >
                Client Feedback
              </Text>
              
              <Heading
                as="h2"
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="bold"
                textAlign="center"
                lineHeight="1.2"
              >
                What People Say
              </Heading>
              
              <Text
                color="brand.text.secondary"
                maxW="600px"
                fontSize={{ base: "md", md: "lg" }}
              >
                Hear from clients about their experience working with me
              </Text>
            </VStack>
          </ScrollAnimationWrapper>

          {/* Testimonials marquee */}
          <Box
            overflow="hidden"
            width="100%"
            position="relative"
            height={{ base: "70vh", md: "auto" }} // Set viewport height for vertical scroll on mobile
          >
            <Flex
              display="flex"
              flexDirection={{ base: "column", md: "row" }} // Column on base, row on md
              gap={{ base: "8", md: "8" }} // Vertical gap on base, horizontal on md
              width={{
                base: "100%", // Full width on base
                md: `calc(200% + ${visibleTestimonials.length > 1 ? "2rem" : "0px"})`
              }}
              height={{ base: "auto", md: "auto" }} // Auto height based on content
              animation={{
                base: `${scrollTop} 30s linear infinite`, // Vertical animation on base
                md: `${scrollLeft} 30s linear infinite` // Horizontal animation on md
              }}
              _hover={{
                animationPlayState: "paused",
              }}
              py={2}
            >
              {animatedDisplayTestimonials.map((testimonial, idx) => (
                <Box
                  key={idx} // Use map index for key in duplicated array
                  // Each card takes up 50% of the visible area, minus half the gap
                  // Base: 1 card visible at a time from the duplicated pair.
                  flexBasis={{
                    base: "auto", // Auto basis on base
                    md: visibleTestimonials.length > 1 ? `calc(${100 / visibleTestimonials.length}% - 1.5rem)` : "100%",
                  }}
                  maxW={{ base: "100%", md: "380px" }}
                  width={{
                    base: "100%", // Full width on base for vertical stack
                    md: "auto" // Let flexBasis control width on md
                  }}
                  flexShrink={0}
                  px={{ base: "0", md: "0" }} // No padding needed if full width
                >
                  <TestimonialCard
                    name={testimonial.name}
                    role={testimonial.role}
                    image={testimonial.image}
                    text={testimonial.text}
                    // Pass original index for ScrollAnimationWrapper if its delay logic is based on it
                    index={idx % visibleTestimonials.length}
                  />
                </Box>
              ))}
            </Flex>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default TestimonialsSection; 